import React, { useEffect, useState } from 'react';
import {
	ChangeSet,
	ChangeSetEntityChangeType,
	DataTypeEntityOption,
	DataTypeState,
	EntityId,
	EntityType,
	ProjectState,
	resolveEntityName,
	OutputMapState,
	READABLE_ENTITY_TYPES,
	OutputMapParentChildRelation,
	EntityInstanceErrorCode,
	lowercaseFirstLetter,
	toSentenceCase,
	StateMutationAction,
} from '@elyx-code/project-logic-tree';
import { Alert, Collapse, Tooltip as MuiTooltip } from '@mui/material';
import { EditorService } from '../../../services/editor';
import DataTypeBuilder, {
	AllowedDataTypeListTypes,
	ILiteralValueOption,
} from '../../data-type/DataTypeBuilder';
import Tooltip from '../../../lib/tooltip/Tooltip';
import Button from '../../../lib/button/Button';
import { useParams } from 'react-router-dom';
import EntityIcon from '../../entity-icon/EntityIcon';
import EntityDialogSectionHeader from '../../EntityDialogSectionHeader';
import WarningIcon from '../../../assets/warning-sign-24px.png';
import DynamicValue from '../../dynamic-value/DynamicValue';
import { DataTypeBuilderOption } from '../../../services/editor/utils';

export interface IOutputMapProps {
	project: EditorService;
	entity?: OutputMapState;
	onClose?: (e: any) => void;
}

export enum OutputMapError {
	IncompleteDataType = 'incomplete-data-type',
}

export function fromOutputMapErrorToReadableMessage(
	error: OutputMapError,
): string {
	switch (error) {
		// case OutputMapError.IncompleteDataType:
		// 	return 'The composition of the data type is incomplete. Please fill in all required fields on each data type element.';
		default:
			return toSentenceCase(error);
	}
}

function calculateOutputMapCreationFormErrors(
	entity: OutputMapState,
): OutputMapError[] {
	const errors: OutputMapError[] = [];

	if (!!entity.dataType) {
		const flatErrors = entity.dataType
			.toFlat()
			.filter(
				(child) =>
					child.type === EntityType.DataType &&
					!child.isResolved &&
					child.id !== entity.dataType.id,
			);

		if (flatErrors.length > 0) {
			errors.push(OutputMapError.IncompleteDataType);
		}
	}

	return errors;
}

// Not all entity types can have outputs with any data type
// Sometimes, like in the case of a search, the data type of its outputs can only be a primitive type or a list of primitives
// This function, given an option, will return some metadata that will help it render the option in the data type builder
export function resolveDataTypeOptionConfigForOutputMap(
	option: DataTypeBuilderOption | ILiteralValueOption,
	output: OutputMapState,
): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeAsTypeConfigForOutputMap(
	output: OutputMapState,
): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeLikeConfigForOutputMap(output: OutputMapState): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeAsListConfigForOutputMap(
	output: OutputMapState,
): {
	allowed: AllowedDataTypeListTypes;
	tooltip: React.ReactNode;
} {
	return {
		allowed: AllowedDataTypeListTypes.All,
		tooltip: null,
	};
}

const OutputMap: React.FC<IOutputMapProps> = ({
	project,
	entity: _entity,
	onClose,
}) => {
	const [updateKey, setUpdateKey] = useState(0);
	const { entityId } = useParams<{
		entityType: EntityType;
		entityId: EntityId;
	}>();

	const entity: OutputMapState | null =
		_entity || (project?.logic?.get(entityId) as OutputMapState | null) || null;

	const [dataType, _setDataType] = useState<DataTypeState | null>(
		entity?.dataType || null,
	);

	const [defaultValueSectionOpen, setDefaultValueSectionOpen] = useState(false);
	const [baseFunctionalitySectionOpen, setBaseFunctionalitySectionOpen] =
		useState(false);

	const [errors, setErrors] = useState<OutputMapError[]>(
		calculateOutputMapCreationFormErrors(entity),
	);

	const setDataType = (
		newDataTypeEntity: DataTypeState | null,
		changeSet: ChangeSet | null,
	) => {
		newDataTypeEntity?.setParent(entity, changeSet);
		entity?.setDataType(newDataTypeEntity, changeSet);

		changeSet?.add(entity, ChangeSetEntityChangeType.Affected);

		_setDataType(newDataTypeEntity);
		setUpdateKey((prev) => prev + 1);

		project.renderAndCloseChangeSet(changeSet);
	};

	useEffect(() => {
		const newErrors = calculateOutputMapCreationFormErrors(entity);
		setErrors(newErrors);
		project.emit(entity.parent.id, {});
	}, [updateKey]);

	return (
		<div
			style={{
				display: 'flex',
				flexGrow: 1,
				height: '100%',
				overflowY: 'scroll',
			}}
		>
			{/* We need the 'span' for the react ref */}
			<span
				style={{
					maxHeight: '100%',
					maxWidth: '100%',
					flexGrow: 1,
					height: '100%',
				}}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						gap: '4px',
						justifyContent: 'space-between',
					}}
				>
					<span
						style={{
							maxWidth: '100%',
							maxHeight: '100%',
							overflowY: 'scroll',
							display: 'flex',
							flexDirection: 'column',
							height: '100%',
						}}
					>
						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							<EntityDialogSectionHeader
								title={`Output name: ${resolveEntityName(
									entity,
									project.logic,
								)}`}
								subtitle={entity.declaration?.description || ''}
								expandable={false}
							/>
						</section>

						<hr
							style={{
								margin: '1px 0',
								border: '1px solid rgba(0,0,0,0.08)',
							}}
						/>

						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: baseFunctionalitySectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title={
									!!entity.declaration
										? `Represents the "${resolveEntityName(
												entity.declaration,
												project.logic,
											)}" ${(entity.declaration?.type === EntityType.Property
												? READABLE_ENTITY_TYPES[EntityType.Property].singular
												: READABLE_ENTITY_TYPES[EntityType.ArgumentDeclaration]
														.singular
											).toLowerCase()}`
										: resolveEntityName(entity, project.logic)
								}
								subtitle={
									!!entity.declaration
										? `This output represents an underlying definition with an existing data type an metadata`
										: entity.parentRelationType ===
											  OutputMapParentChildRelation.Error
											? 'This output is not declared by an underlying error outputed by a base trigger'
											: 'This output is not declared, and is used dynamically in the logic'
								}
								hideShowSuffix='the base functionality section'
								open={baseFunctionalitySectionOpen}
								expandable={!!(entity as OutputMapState).declaration}
								onToggle={() =>
									setBaseFunctionalitySectionOpen(!baseFunctionalitySectionOpen)
								}
								headerStartIcon={
									<EntityIcon
										entity={(entity as OutputMapState).declaration || entity}
										project={project}
									/>
								}
							/>

							<Collapse in={baseFunctionalitySectionOpen}>
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: 20,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<DataTypeBuilder
										project={project}
										topParent={entity}
										dataType={dataType}
										readOnly={!entity.editable}
										baseDataTypes={
											!!(entity as OutputMapState).declaration?.dataType
												? [(entity as OutputMapState).declaration?.dataType]
												: []
										}
										onChangeSet={() => {
											const newChangeSet = project.logic.addChangeSet(
												new ChangeSet(
													project.logic,
													ProjectState.sessionAuthor,
													new Date().toISOString(),
													entity,
													false,
													StateMutationAction.ChangeOutputMapDataType,
												),
											);

											return newChangeSet;
										}}
										onChange={(newDataTypeEntity, changeSet) => {
											setDataType(newDataTypeEntity, changeSet);
										}}
										lists={
											resolveDataTypeAsListConfigForOutputMap(entity).allowed
										}
										getOptionDisabled={(option) =>
											resolveDataTypeOptionConfigForOutputMap(option, entity)
												.disabled
										}
										getListDisabledTooltip={(option) =>
											resolveDataTypeAsListConfigForOutputMap(entity).tooltip
										}
										getOptionTooltip={(option) =>
											resolveDataTypeOptionConfigForOutputMap(option, entity)
												.tooltip
										}
										disallowAsType={
											resolveDataTypeAsTypeConfigForOutputMap(entity).disabled
										}
										getAsTypeDisabledTooltip={() =>
											resolveDataTypeAsTypeConfigForOutputMap(entity).tooltip
										}
										disallowLike={
											resolveDataTypeLikeConfigForOutputMap(entity).disabled
										}
										getLikeDisabledTooltip={() =>
											resolveDataTypeLikeConfigForOutputMap(entity).tooltip
										}
									/>
								</div>
							</Collapse>
						</section>

						<hr
							style={{
								margin: '1px 0',
								border: '1px solid rgba(0,0,0,0.08)',
							}}
						/>

						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: defaultValueSectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title='Default value'
								subtitle='Set the default value for this output'
								hideShowSuffix='the default value section'
								open={defaultValueSectionOpen}
								onToggle={() =>
									setDefaultValueSectionOpen(!defaultValueSectionOpen)
								}
								headerStartIcon={
									!!entity.errors.find(
										(error) =>
											error.code ===
											EntityInstanceErrorCode.MissingRequiredValue,
									) ? (
										<MuiTooltip
											title='Missing required value'
											placement='top'
											arrow
										>
											<img
												style={{ width: 20, height: 20 }}
												id={'logic-error-icon--' + entity.id}
												src={WarningIcon}
												className={'warning-icon logic-error-icon'}
											/>
										</MuiTooltip>
									) : null
								}
							/>

							<Collapse in={defaultValueSectionOpen}>
								<div
									style={{
										marginTop: 6,
										display: 'flex',
										flexDirection: 'column',
										gap: 20,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<DynamicValue
										valueOwner={entity}
										project={project}
										onlyDefault
										parentDisabledReasons={[]}
									/>
								</div>
							</Collapse>
						</section>
					</span>

					{errors.length > 0 && (
						<div
							style={{
								marginTop: 20,
								marginRight: 40,
								marginLeft: 80,
							}}
						>
							<Alert severity='error'>
								{errors.length === 1 ? (
									<span
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 6,
										}}
									>
										<h4
											style={{
												margin: 0,
												display: 'flex',
											}}
										>
											Error:
										</h4>
										<p
											style={{
												margin: 0,
												display: 'flex',
											}}
										>
											{fromOutputMapErrorToReadableMessage(errors[0])}
										</p>
									</span>
								) : (
									<>
										<h4
											style={{
												margin: 0,
											}}
										>
											Errors
										</h4>
										<br className='quarter-space' />
										<ul
											style={{
												listStyleType: 'none',
												padding: 0,
											}}
										>
											{errors.map((error, index) => (
												<li
													key={index}
													style={{
														display: 'flex',
														alignItems: 'center',
													}}
												>
													{/* Circle */}
													<span
														style={{
															fontSize: 10,
															marginRight: 5,
														}}
													>
														&#9679;
													</span>
													<p
														style={{
															margin: 0,
															// fontSize: 10,
														}}
													>
														{fromOutputMapErrorToReadableMessage(error)}
													</p>
												</li>
											))}
										</ul>
									</>
								)}
							</Alert>
						</div>
					)}

					<div
						style={{
							alignSelf: 'flex-start',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-end',
							width: '100%',
							gap: '16px',
							padding: '0 40px',
						}}
					>
						<Button
							id='output-map--done-button'
							onClick={(e: any) => {
								onClose?.(e);
							}}
							color='primary'
							disabled={errors.length > 0}
						>
							Done
						</Button>
						<Tooltip
							anchorQuerySelector='#output-map--done-button'
							placement='bottom'
							openDelay={100}
						>
							{errors.length > 0
								? `Resolve the errors to save the ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[entity.type].singular,
									)}`
								: `Save the ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[entity.type].singular,
									)}`}
						</Tooltip>
					</div>
				</div>
			</span>
		</div>
	);
};

export default OutputMap;
